import {api} from './api';
import {EntityType, Entry, Repository, RepositoryByIdResponse, RepositoryEntry} from '../types';
import type {RootState} from '../store';
import {entriesAddMany, entriesUpdateMany, entriesAppendMany} from '../containers/contentSlice';

const noteTagId = (noteId: string) => `note:${noteId}`;

export type RepositoryListItem = Repository & {
	entryCount: number;
};

export interface RepositoryCreateDto {
	title: string;
	singular: string;
	rank?: number;
	color?: string | null;
	isBookmarked?: boolean;
}

export interface RepositoryUpdateDto {
	id: string;
	title?: string;
	singular?: string;
	rank?: number;
	color?: string | null;
	isBookmarked?: boolean;
}

export interface AiDiscoveryPropertyDto {
	id: string;
	slug: string;
	label: string;
	type: string;
	targetRepositoryId: string | null;
	targetRepositoryTitle: string | null;
}

export interface AiDiscoveryRepositoryDto {
	repositoryId: string;
	title: string;
	singular: string;
	entryCount: number;
	properties: AiDiscoveryPropertyDto[];
}

export interface AiDiscoveryResponseDto {
	repositories: AiDiscoveryRepositoryDto[];
}

export interface RepositoryEntriesRowDto {
	id: string;
	label: string;
	data: Record<string, unknown>;
	rank: string | number;
	isArchived: boolean;
}

export interface GetRepositoryEntriesResponseDto {
	repositoryId: string;
	title: string;
	singular: string;
	rowCount: number;
	returnedRows: number;
	hasMore: boolean;
	rows: RepositoryEntriesRowDto[];
}

function applyRepositoryEntryValuesToListRow(row: RepositoryEntriesRowDto, values: Record<string, unknown>) {
	if (!values || typeof values !== 'object') {
		return;
	}
	for (const [k, v] of Object.entries(values)) {
		if (k === 'rank') {
			row.rank = v as string | number;
		} else if (k === 'label') {
			row.label = v != null ? String(v) : row.label;
		} else if (k === 'isArchived') {
			row.isArchived = Boolean(v);
		} else {
			row.data = { ...row.data, [k]: v };
		}
	}
}

/**
 * Composite tags for currently cached `getRepositoryEntriesByFilter` args on this repository, matching
 * the second tag each of those queries provides in `providesTags` below. Filtered/sorted lists need a
 * refetch after an update because the edited field may change which rows match the filter or where they
 * sort — but the plain `getRepositoryEntries` list (tagged only with the bare repositoryId) has none of
 * that, so it deliberately isn't included: invalidating it would refetch every consumer of the unfiltered
 * list (e.g. AddRepositoryEntryDialog) even though the optimistic patch already applied the change to that one row.
 */
function filteredRepositoryEntriesListTags(
	getState: () => RootState,
	repositoryId: string,
): { type: 'RepositoryEntries'; id: string }[] {
	const state = getState();
	const selectCachedArgsForQuery = (api.util as { selectCachedArgsForQuery?: (s: RootState, name: string) => unknown[] })
		.selectCachedArgsForQuery;
	if (!selectCachedArgsForQuery) {
		return [];
	}
	return (
		selectCachedArgsForQuery(state, 'getRepositoryEntriesByFilter') as Array<{
			repositoryId: string;
			includeArchived?: boolean;
			dataFilter?: string;
		}>
	)
		.filter((arg) => arg.repositoryId === repositoryId)
		.map((arg) => ({
			type: 'RepositoryEntries' as const,
			id: `${arg.repositoryId}:${String(arg.includeArchived !== false)}:${arg.dataFilter ?? ''}`,
		}));
}

/**
 * Optimistic RTK patches for a repository entry row (reorder rank, field edits, etc.).
 * Unlike `propertyDefinitionsUpdateMany`, which only patches `getRepositoryById` (defs live on that payload),
 * entry rows live on separate list queries — often several cached `getRepositoryEntriesByFilter` args plus
 * `getRepositoryEntries` — so we patch every cached list that contains the row (same undo-on-error pattern).
 */
function patchAllCachedRepositoryEntriesLists(
	dispatch: (a: unknown) => { undo: () => void },
	getState: () => RootState,
	entryId: string,
	values: Record<string, unknown>,
): { undo: () => void }[] {
	const state = getState();
	const selectCachedArgsForQuery = (api.util as { selectCachedArgsForQuery?: (s: RootState, name: string) => unknown[] })
		.selectCachedArgsForQuery;
	if (!selectCachedArgsForQuery) {
		return [];
	}
	const patchResults: { undo: () => void }[] = [];
	for (const filterArg of selectCachedArgsForQuery(state, 'getRepositoryEntriesByFilter')) {
		patchResults.push(
			dispatch(
				(api.util.updateQueryData as (
					endpointName: 'getRepositoryEntriesByFilter',
					arg: typeof filterArg,
					updateRecipe: (draft: GetRepositoryEntriesResponseDto) => void,
				) => ReturnType<typeof api.util.updateQueryData>)(
					'getRepositoryEntriesByFilter',
					filterArg as never,
					(draft) => {
						const row = draft.rows.find((r) => r.id === entryId);
						if (row) {
							applyRepositoryEntryValuesToListRow(row, values);
						}
					},
				),
			),
		);
	}
	for (const repoArg of selectCachedArgsForQuery(state, 'getRepositoryEntries')) {
		patchResults.push(
			dispatch(
				(api.util.updateQueryData as (
					endpointName: 'getRepositoryEntries',
					arg: string,
					updateRecipe: (draft: GetRepositoryEntriesResponseDto) => void,
				) => ReturnType<typeof api.util.updateQueryData>)(
					'getRepositoryEntries',
					repoArg as string,
					(draft) => {
						const row = draft.rows.find((r) => r.id === entryId);
						if (row) {
							applyRepositoryEntryValuesToListRow(row, values);
						}
					},
				),
			),
		);
	}
	return patchResults;
}

export const repositoryEntriesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		createRepository: builder.mutation({
			query: (payload: RepositoryCreateDto) => ({
				url: 'repositories',
				method: 'POST',
				body: payload,
			}),
			extraOptions: { maxRetries: 0 },
			invalidatesTags: ['Repositories', 'Repository'],
		}),
		updateRepository: builder.mutation<unknown, RepositoryUpdateDto>({
			query: (payload: RepositoryUpdateDto) => ({
				url: `repositories/${payload.id}`,
				method: 'PUT',
				body: payload,
			}),
			// This part takes care of local optimistic update (after reordering is committed).
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				const { id, ...patch } = arg;
				const listPatch = dispatch(
					(api.util.updateQueryData as (
						endpointName: 'loadRepositories2',
						arg: void,
						updateRecipe: (draft: RepositoryListItem[]) => void,
					) => ReturnType<typeof api.util.updateQueryData>)(
						'loadRepositories2',
						undefined,
						(draft) => {
							const repo = draft.find((r) => r.id === id);
							if (repo) {
								Object.assign(repo, patch);
							}
						},
					),
				);
				const detailPatch = dispatch(
					(api.util.updateQueryData as (
						endpointName: 'getRepositoryById',
						arg: string,
						updateRecipe: (draft: RepositoryByIdResponse) => void,
					) => ReturnType<typeof api.util.updateQueryData>)(
						'getRepositoryById',
						id,
						(draft) => {
							Object.assign(draft.repository, patch);
						},
					),
				);
				dispatch(entriesUpdateMany([{ id, ...patch }]));
				try {
					await queryFulfilled;
				} catch {
					listPatch.undo();
					detailPatch.undo();
				}
			},
			extraOptions: { maxRetries: 0 },
			/** Match property-definitions: do not invalidate `Repositories` or the list refetches and flickers before ranks settle. */
			invalidatesTags: (result, error, arg) => [{ type: 'Repository' as const, id: arg.id }],
		}),
		deleteRepository: builder.mutation({
			query: (id: string) => ({
				url: `repositories/${id}`,
				method: 'DELETE',
			}),
			extraOptions: { maxRetries: 0 },
			invalidatesTags: ['Repositories', 'Repository'],
		}),
		loadRepositories: builder.mutation<RepositoryListItem[], void>({
			query: () => ({
				url: 'repositories',
				method: 'GET',
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }: any) {
				try {
					const { data } = await queryFulfilled;
					if (Array.isArray(data)) {
						dispatch(entriesAppendMany(data));
					}
				} catch {
					// ignore, error handling is global
				}
			},
			extraOptions: { maxRetries: 0 },
		}),
		loadRepositories2: builder.query<RepositoryListItem[], void>({
			query: () => ({
				url: 'repositories',
				method: 'GET',
			}),
			extraOptions: { maxRetries: 0 },
			providesTags: ['Repositories'],
		}),
		loadRepository: builder.mutation<RepositoryByIdResponse, string>({
			query: (repositoryId: string) => ({
				url: `repositories/${repositoryId}`,
				method: 'GET',
				params: {
					includeArchived: true
				}
			}),
			extraOptions: { maxRetries: 0 }
		}),
		/** `GET repositories/:id` — repository metadata, property defs, and populated entries for that repo. */
		getRepositoryById: builder.query<RepositoryByIdResponse, string>({
			query: (repositoryId: string) => ({
				url: `repositories/${repositoryId}`,
				method: 'GET',
				params: {
					includeArchived: true
				}
			}),
			providesTags: (_result, _error, repositoryId) => [
				{type: 'Repository' as const, id: repositoryId},
			],
			extraOptions: { maxRetries: 0 }
		}),
		getRepositoryByEntryId: builder.query<RepositoryByIdResponse, string>({
			query: (entryId: string) => ({
				url: `repositories/by-entry/${entryId}`,
				method: 'GET',
			}),
			providesTags: (_result, _error, entryId) => [
				{type: 'RepositoryEntry' as const, id: entryId},
			],
			extraOptions: { maxRetries: 0 }
		}),
		getRepositoriesAiDiscovery: builder.query<AiDiscoveryResponseDto, void>({
			query: () => ({
				url: 'repositories/ai/discovery',
				method: 'GET',
			}),
			extraOptions: { maxRetries: 0 },
		}),
		getRepositoryEntries: builder.query<GetRepositoryEntriesResponseDto, string>({
			query: (repositoryId: string) => ({
				url: `repositories/${repositoryId}/entries`,
				method: 'GET',
			}),
			providesTags: (_result, _error, repositoryId) => [
				{type: 'RepositoryEntries' as const, id: repositoryId},
			],
			extraOptions: { maxRetries: 0 },
		}),
		getRepositoryEntriesByFilter: builder.query<
			GetRepositoryEntriesResponseDto,
			{
				repositoryId: string;
				dataFilter?: string;
				includeArchived?: boolean;
				sortBy?: string;
				sortDir?: 'asc' | 'desc';
			}
		>({
			query: ({ repositoryId, dataFilter, includeArchived, sortBy, sortDir }) => ({
				url: `repositories/${repositoryId}/entries`,
				method: 'GET',
				params: {
					...(dataFilter ? { dataFilter } : {}),
					includeArchived: includeArchived !== false,
					...(sortBy ? { sortBy } : {}),
					...(sortDir ? { sortDir } : {}),
				},
			}),
			providesTags: (_result, _error, args) => [
				{
					type: 'RepositoryEntries' as const,
					id: args.repositoryId,
				},
				{
					type: 'RepositoryEntries' as const,
					id: `${args.repositoryId}:${String(args.includeArchived !== false)}:${args.dataFilter ?? ''}`,
				},
			],
			extraOptions: { maxRetries: 0 },
		}),
		loadRepositoryEntry2: builder.mutation({
			query: (entryIds: string[]) => ({
				url: 'repositoryEntries2',
				params: {
					entryIds,
				},
				method: 'GET',
			}),
			extraOptions: { maxRetries: 0 }
		}),
		loadRepositoryEntry3: builder.query<GetRepositoryEntriesResponseDto, string>({
			query: (entryId: string) => ({
				url: `repositoryEntries3/${entryId}`,
				method: 'GET',
			}),
			extraOptions: { maxRetries: 0 },
			providesTags: (_result, _error, entryId) => [
				{type: 'RepositoryEntry' as const, id: entryId},
			],
		}),
		repositoryEntryCreate: builder.mutation({
			query: (payload: { entry: Entry, properties: Entry[] }) => ({
				url: 'repositoryEntries',
				method: 'POST',
				body: payload,
			}) as any,
			async onQueryStarted(
				arg: { entry: Entry, properties: Entry[] },
				{
					dispatch,
				}: any
			) {
				dispatch(entriesAddMany([arg.entry, ...arg.properties]));
			},
			extraOptions: { maxRetries: 0 },
			invalidatesTags: (_result: any, _error: any, arg: { entry: RepositoryEntry }) => [
				{ type: 'RepositoryEntries' as const, id: arg.entry.repositoryId },
				{ type: 'RepositoryEntryCount' as const, id: arg.entry.repositoryId },
			],
		} as any),
		repositoryEntryUpdate: builder.mutation({
			query: (payload: { id: string, values: any}) => ({
				url: `repositoryEntries/${payload.id}`,
				method: 'PUT',
				body: payload.values,
			}) as any,
			async onQueryStarted(
				arg: {
					id: string;
					values: any;
					/** Display-shaped overrides for the optimistic patch, e.g. labeled relation values where `values` only has ids. */
					optimisticValues?: Record<string, unknown>;
					repositoryId?: string | null;
					skipListRefetch?: boolean;
				},
				{ dispatch, queryFulfilled, getState }: any,
			) {
				// Same flow as `propertyDefinitionsUpdateMany`: patch RTK → await → undo patches on failure → invalidate on success.
				const patchValues = { ...arg.values, ...arg.optimisticValues };
				const patchResults = [
					...patchAllCachedRepositoryEntriesLists(dispatch, getState, arg.id, patchValues),
					dispatch(
						(api.util.updateQueryData as (
							endpointName: 'loadRepositoryEntry3',
							arg: string,
							updateRecipe: (draft: GetRepositoryEntriesResponseDto) => void,
						) => ReturnType<typeof api.util.updateQueryData>)(
							'loadRepositoryEntry3',
							arg.id,
							(draft) => {
								const row = draft.rows.find((r) => r.id === arg.id);
								if (row) {
									applyRepositoryEntryValuesToListRow(row, patchValues);
								}
							},
						),
					),
				];
				try {
					await queryFulfilled;
				} catch {
					for (const p of patchResults) {
						p.undo();
					}
					return;
				}
				// `skipListRefetch` is for callers (TimelineView's rank/date drags) whose own rendering never
				// reads row order from these cached lists, so the optimistic patch above is already enough —
				// invalidating here would force a full list refetch for no visible benefit, just a loading flash.
				if (arg.skipListRefetch) return;
				const entry = (getState() as RootState).content.entries.find(
					(e) => e.id === arg.id && e.type === EntityType.RepositoryEntry,
				) as RepositoryEntry | undefined;
				const repoId = entry?.repositoryId ?? arg.repositoryId ?? undefined;
				if (repoId) {
					dispatch(api.util.invalidateTags(filteredRepositoryEntriesListTags(getState, repoId)));
				}
			},
			extraOptions: { maxRetries: 0 },
			invalidatesTags: (_result: unknown, _error: unknown, arg: { id: string }) => [
				{ type: 'RepositoryEntry' as const, id: arg.id },
			],
		} as any),
		repositoryEntryArchive: builder.mutation({
			query: ({ entryId }: { entryId: string; repositoryId: string }) => ({
				url: `repositoryEntries/${entryId}/archive`,
				method: 'PUT',
			}) as any,
			// Only patches the cached list rows in place (like repositoryEntryUpdate's skipListRefetch
			// path) rather than invalidating `RepositoryEntries` — every consumer already derives its
			// archived/non-archived split client-side from the `isArchived` flag on each row, so a full
			// list refetch would just be a loading flash with no visible correctness benefit.
			async onQueryStarted(
				{ entryId }: { entryId: string; repositoryId: string },
				{ dispatch, queryFulfilled, getState }: any,
			) {
				dispatch(entriesUpdateMany([{ id: entryId, isArchived: true }]));
				const patchResults = patchAllCachedRepositoryEntriesLists(dispatch, getState, entryId, { isArchived: true });
				try {
					await queryFulfilled;
				} catch {
					for (const p of patchResults) {
						p.undo();
					}
				}
			},
			extraOptions: { maxRetries: 0 },
			invalidatesTags: (_result: unknown, _error: unknown, arg: { entryId: string; repositoryId: string }) => [
				{ type: 'RepositoryEntry' as const, id: arg.entryId },
			],
		} as any),
		repositoryEntryUnarchive: builder.mutation({
			query: ({ entryId }: { entryId: string; repositoryId: string }) => ({
				url: `repositoryEntries/${entryId}/unarchive`,
				method: 'PUT',
			}) as any,
			async onQueryStarted(
				{ entryId }: { entryId: string; repositoryId: string },
				{ dispatch, queryFulfilled, getState }: any,
			) {
				dispatch(entriesUpdateMany([{ id: entryId, isArchived: false }]));
				const patchResults = patchAllCachedRepositoryEntriesLists(dispatch, getState, entryId, { isArchived: false });
				try {
					await queryFulfilled;
				} catch {
					for (const p of patchResults) {
						p.undo();
					}
				}
			},
			extraOptions: { maxRetries: 0 },
			invalidatesTags: (_result: unknown, _error: unknown, arg: { entryId: string; repositoryId: string }) => [
				{ type: 'RepositoryEntry' as const, id: arg.entryId },
			],
		} as any),
		repositoryEntryDelete: builder.mutation<
			{ deletedIds: string[] },
			{ id: string; repositoryId?: string }
		>({
			query: ({ id }) => ({
				url: `repositoryEntries/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			async onQueryStarted(
				{ id, repositoryId }: { id: string; repositoryId?: string },
				{ dispatch, getState }: any,
			) {
				const tags: Array<
					| 'RepositoryEntryCount'
					| { type: 'Repository'; id: string }
					| { type: 'RepositoryEntry'; id: string }
					| { type: 'RepositoryEntries'; id: string }
					| { type: 'Notes'; id: string }
				> = ['RepositoryEntryCount'];
				const allEntries = (getState() as RootState).content.entries as Entry[];
				const deletedEntry = allEntries.find((entry) => entry.id === id);
				const parentId = String(deletedEntry?.parentId ?? '');
				if (parentId) {
					tags.push({ type: 'Notes', id: noteTagId(parentId) });
				}
				const repoId =
					repositoryId ??
					(deletedEntry?.type === EntityType.RepositoryEntry
						? (deletedEntry as RepositoryEntry).repositoryId
						: undefined);
				if (repoId) {
					tags.push(
						{ type: 'Repository', id: repoId },
						{ type: 'RepositoryEntry', id },
						{ type: 'RepositoryEntries', id: repoId },
					);
				}
				dispatch(api.util.invalidateTags(tags));
			},
			extraOptions: { maxRetries: 0 },
		}),
	}),
});

export const {
	useLoadRepositoriesMutation,
	useLoadRepositoryMutation,
	useGetRepositoryByIdQuery,
	useGetRepositoryByEntryIdQuery,
	useGetRepositoriesAiDiscoveryQuery,
	useGetRepositoryEntriesQuery,
	useGetRepositoryEntriesByFilterQuery,
	useLoadRepositoryEntry2Mutation,
	useLoadRepositoryEntry3Query,
	useLoadRepositoryEntry3Query: useLoadRepositoryEntryQuery,
	useRepositoryEntryCreateMutation,
	useRepositoryEntryUpdateMutation,
	useRepositoryEntryArchiveMutation,
	useRepositoryEntryUnarchiveMutation,
	useRepositoryEntryDeleteMutation,
	useLoadRepositories2Query: useLoadRepositoriesQuery,
	useCreateRepositoryMutation,
	useUpdateRepositoryMutation,
	useDeleteRepositoryMutation,
} = repositoryEntriesApi;
